import PropTypes from 'prop-types';
import semVerSatisfies from 'semver/functions/satisfies';
import pkg from '../../../package.json';
/**
 * @typedef {import('../../utils/props/variantProp.js').AppearanceSet} AppearanceSet
 * @typedef {import('../../utils/props/tokens.js').TokensProp} TokensProp
 * @typedef {import('../../utils/props/tokens.js').TokensSet} TokensSet
 */

/**
 * General utilities around working with theme tokens
 */

export const getComponentTheme = (theme, componentName) => {
  var _theme$metadata;

  // Give clear and understandable error messages for common dev errors, for example,
  // typo in component name, missing export or accessing old version of theme
  if (!theme) {
    throw new Error(`Called useTheme's getStyle on "${componentName}" with no theme provided`);
  }

  const themeName = ((_theme$metadata = theme.metadata) === null || _theme$metadata === void 0 ? void 0 : _theme$metadata.name) || '';

  if (!theme.components) {
    throw new Error(`Theme "${themeName}" has no components defined (looking for "${componentName}")`);
  }

  const componentTheme = theme.components[componentName];

  if (!componentTheme) {
    throw new Error(`Theme "${themeName}" does not have styles for component "${componentName}"`);
  }

  return componentTheme;
};
export const doesThemeConditionApply = (_ref, appearances) => {
  let [key, value] = _ref;
  // use null rather than undefined so we can serialise the value in themes
  const appearanceValue = appearances[key] ?? null;
  return Array.isArray(value) ? value.includes(appearanceValue) : value === appearanceValue;
};
export const doesThemeRuleApply = (rule, appearances) => Object.entries(rule.if).every(condition => doesThemeConditionApply(condition, appearances));
/**
 * Turns a tokens prop (which may be either a tokens object or a tokens getter function)
 * into one resolved tokens object, based on current appearances state.
 *
 * This is used inside `useThemeTokens` and `useThemeTokensCallback` for handling the `tokens`
 * props of themed components. It may also be used directly in cases where a non-themed component
 * needs to resolve a tokens prop that may be a tokens getter function or a resolved tokens object.
 *
 * A second tokens prop of tokens overrides may optionally be passed; it will be resolved to an object
 * based on provided appearance state and then shallow-merged over the first set of tokens.
 *
 * @param {TokensProp} defaultTokens - a set of tokens or tokens getter function which may be overridden
 * @param {AppearanceSet} [appearances] - optional appearance set to pass to tokens getter functions
 * @param {TokensProp} [tokenOverrides] - optional set of tokens or tokens getter function to override the default
 * @returns {TokensSet} - object containing resolved tokens with overrides applied
 */

export const resolveThemeTokens = function (defaultTokens) {
  let appearances = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  let tokenOverrides = arguments.length > 2 ? arguments[2] : undefined;

  const resolve = tokens => typeof tokens === 'function' ? tokens(appearances) : tokens;

  if (!tokenOverrides) return resolve(defaultTokens);
  return Object.entries(resolve(tokenOverrides)).reduce((mergedTokens, _ref2) => {
    let [tokenName, tokenValue] = _ref2;
    return tokenValue === undefined ? mergedTokens : { ...mergedTokens,
      [tokenName]: tokenValue
    };
  }, resolve(defaultTokens));
};
/**
 * Gives a prop types error if a set of resolved tokens doesn't match a provided prop
 * types validator.
 *
 * In production, this is a no-op (PropTypes.checkPropTypes is automatically replaced
 * with a no-op function).
 *
 * @param {TokensSet} themeTokens - object containing resolved theme tokens
 * @param {function} validator - PropTypes validator function
 * @param {string} componentName - identifier to display in PropType error messages
 */

export const validateThemeTokens = (themeTokens, validator, componentName) => {
  PropTypes.checkPropTypes({
    tokens: validator
  }, {
    tokens: themeTokens
  }, 'resolved token', componentName);
  return themeTokens;
};
/**
 * Merges variants over states. Must be merged in that order to allow static showcases of a state,
 * e.g. `<Button variant={{ pressed: true }} />` where button's pressed state is `false` by default.
 * Returns an empty object if both variants and states are undefined.
 *
 * @param {AppearanceSet} [variants]
 * @param {AppearanceSet} [states]
 * @returns {AppearanceSet}
 */

export const mergeAppearances = function () {
  let variants = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  let states = arguments.length > 1 ? arguments[1] : undefined;
  return states ? { ...states,
    ...variants
  } : variants;
};
/**
 * Get a set of theme tokens by applying a theme's component rules based on a component's variants and state.
 *
 * This is used internally in `useThemeTokens` and `useThemeTokensCallback` and should not normally be called directly.
 *
 * @param {object} componentTheme - object containing a theme's `tokens` and `rules` for a component
 * @param {object|function} [tokensProp] - UDS tokens prop containing theming overrides
 * @param {object} [variants] - UDS variant prop containing the variant options for this component instance
 * @param {object} [states] - object containing current state options for this component instance
 * @returns {object} Set of resolved theme tokens
 */

export const getThemeTokens = function (_ref3, tokensProp) {
  let {
    rules = [],
    tokens: defaultThemeTokens = {}
  } = _ref3;
  let variants = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  let states = arguments.length > 3 ? arguments[3] : undefined;
  const appearances = mergeAppearances(variants, states); // TODO: if in dev mode, validate the appearances and provided propTokens
  // Get the theme's default tokens set and merge tokens from applicable theme rules over it

  const themeTokens = rules.reduce((mergedTokens, rule) => doesThemeRuleApply(rule, appearances) ? { ...mergedTokens,
    ...rule.tokens
  } : mergedTokens, defaultThemeTokens);
  return resolveThemeTokens(themeTokens, appearances, tokensProp);
};
export const toArray = strOrArr => Array.isArray(strOrArr) ? strOrArr : [strOrArr];
/**
 * Throws an error if the theme was built with an incompatible version of @telus-uds/system-theme-tokens
 *
 * This is used internally by the ThemeProvider to force a fast failure when an incompatible version is detected.
 *
 * Version compatibility is calculated with semver.satisfies.
 * Refer to https://github.com/npm/node-semver and https://semver.npmjs.com/ for details about how
 * semver compatibility.
 *
 * @param {object} theme - UDS theme built for react-native
 */

export const validateThemeTokensVersion = theme => {
  var _theme$metadata2;

  const expectedThemeTokensVersion = pkg.dependencies['@telus-uds/system-theme-tokens'];
  const actualThemeTokensVersion = theme === null || theme === void 0 ? void 0 : (_theme$metadata2 = theme.metadata) === null || _theme$metadata2 === void 0 ? void 0 : _theme$metadata2.themeTokensVersion;

  if (!semVerSatisfies(actualThemeTokensVersion, expectedThemeTokensVersion)) {
    throw new Error(`Invalid UDS token schema version detected.

The UDS base components ${pkg.name} v${pkg.version} are only compatible with UDS themes that are built with @telus-uds/system-theme-tokens version that is semver compatible with ${expectedThemeTokensVersion}. The current theme was built with @telus-uds/system-theme-tokens v${actualThemeTokensVersion}.

If you see this error than most likely you have attempted to install ${pkg.name} and a UDS theme manually because you are building a multi-brand application. If you are building a single brand application, consider installing the brand specific design system package such as @telus-uds/ds-allium. For more information, see https://github.com/telus/universal-design-system/blob/main/docs/docs/multi-brand-usage.md`);
  }
};